import { dollars } from '@/lib/format';
import type { NotificationData } from '@/types/api';

/**
 * Human-readable title/body for a notification, derived from its untyped `data`
 * payload. Covers every notification type the backend dispatches; unknown types
 * fall back to whatever title the payload carries.
 */
export function textFor(n: NotificationData): { title: string; body: string } {
  const d = n.data;
  const event = (d.event as string | undefined) ?? n.type;
  const clientName = (d.client_name as string | null | undefined) ?? '';
  const docTitle = d.title as string | null | undefined;
  const amount = typeof d.amount_cents === 'number' ? d.amount_cents : null;

  switch (event) {
    case 'approvable.signed':
      return {
        title: `${d.signed_name ?? 'Someone'} signed ${docTitle ?? 'an approvable'}`,
        body: clientName,
      };
    case 'approvable.declined':
      return {
        title: `${clientName || 'A client'} declined ${docTitle ?? 'an approvable'}`,
        body: (d.comment as string) ?? '',
      };
    case 'approvable.deferred':
      return {
        title: `${clientName || 'A client'} chose "Maybe later" on ${docTitle ?? 'a proposal'}`,
        body: (d.comment as string) ?? '',
      };
    case 'invoice.paid':
      return {
        title: `Payment received: ${dollars(amount)}`,
        body: `${clientName || 'Client'} · ${docTitle ?? 'Invoice'}`,
      };
    case 'invoice.payment_received':
      return {
        title: `Payment received: ${dollars(amount)}`,
        body: `${clientName || 'Client'} · Invoice ${d.invoice_number ?? ''}`.trim(),
      };
    case 'invoice.card_authorized':
      return {
        title: `Card authorized: ${dollars(amount)}`,
        body: `${d.signer_name ?? clientName} · ${d.card_brand ?? 'Card'} ····${d.card_last4 ?? ''}`,
      };
    case 'client_request.submitted':
      return {
        title: docTitle ?? `New request from ${clientName || 'a client'}`,
        body: (d.subject as string) ?? '',
      };
    case 'blog_post.submitted':
      return {
        title: docTitle ?? `${d.post_title ?? 'A post'} submitted for review`,
        body: (d.site_name as string) ?? clientName,
      };
    case 'voicemail.received':
      return {
        title: docTitle ?? 'New voicemail',
        body: (d.from as string) ?? '',
      };
    case 'web_inquiry.received':
      return {
        title: docTitle ?? `Inquiry from ${d.lead_name ?? 'website'}`,
        body: (d.message_preview as string) ?? '',
      };
    default:
      return { title: docTitle ?? n.type, body: clientName };
  }
}

/**
 * In-app destination for a notification, or null when there's nothing specific
 * to open. Used for both the flyout and the full notifications page.
 */
export function linkFor(n: NotificationData): string | null {
  const d = n.data;
  const event = (d.event as string | undefined) ?? n.type;
  const clientId = d.client_id as number | undefined;

  if (event === 'web_inquiry.received') {
    const leadId = d.lead_id as number | undefined;
    return leadId ? `/leads/${leadId}` : null;
  }
  if (event === 'blog_post.submitted') {
    const postId = d.post_id as number | undefined;
    return postId ? `/blog/${postId}` : null;
  }
  if (event === 'client_request.submitted') {
    return '/requests';
  }
  if (event === 'voicemail.received') {
    return '/calls';
  }
  if (event.startsWith('approvable.') || event.startsWith('invoice.')) {
    return clientId ? `/clients/${clientId}?tab=approvables` : null;
  }
  return clientId ? `/clients/${clientId}` : null;
}

/** Compact relative timestamp (just now / 5m ago / 3h ago / 2d ago / date). */
export function formatWhen(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return date.toLocaleDateString();
}
